import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { PageTabRail } from '../../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../../components/router/router-tabbed-navigation';
import { useDocumentPageContext } from '../../../../../../../../../../features/document/document-page-context';
import { DocumentsTable, type DocumentsTableHeaderProps } from '../../../../../../../../../../features/document/table/documents-table';
import { AccountYearSelect } from '../../../../../../../../../../features/registration/account/account-year-select/account-year-select';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';
import type { ViewContext } from '../../../../../../../../../../util/view-context';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/document/_document/tax')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.RegistrationAccountDocuments,
      label: 'Tax Forms',
      order: 2,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.RegistrationAccountDocuments>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}-documents-tax`,
        label: 'Documents',
        to: `/registration/account/${params.accountId}/document/tax`,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Tax Forms'),
  component: AccountTaxDocumentsList,
});

function TableHeader(_props: DocumentsTableHeaderProps) {
  const { accountId } = Route.useParams();
  const { year, setYear } = useDocumentPageContext();

  return (
    <PageTabRail
      actions={
        <AccountYearSelect
          accountId={accountId}
          label="Document Year"
          name="tax-form-year-select"
          onChange={(e) => {
            const newYear = parseInt(e.target.value, 10);
            if (!Number.isNaN(newYear)) {
              setYear(newYear);
            }
          }}
          value={year.toString()}
        />
      }
      group={TabbedNavigationGroup.RegistrationAccountDocuments}
    />
  );
}

function AccountTaxDocumentsList() {
  const { accountId } = Route.useParams();
  const { year } = useDocumentPageContext();
  const viewContext: ViewContext = useMemo(
    () => ({
      kind: 'account',
      accountId,
    }),
    [accountId],
  );

  return <DocumentsTable documentType="taxDocument" HeaderActions={TableHeader} renderWhenEmpty="No Tax Forms" view={viewContext} year={year} />;
}
